/**
 * The service-level trade-off's contract — bata-ui issue 308, shared contract of bata-ui
 * issue 307 (owner rulings 2026-10-06), read built by bata-platform issues 570 and 571.
 * `docs/adr/6015` (the two in-season pages) and `docs/adr/9050` (the two chart forms).
 *
 * ══ PINNED 2026-10-06 ═════════════════════════════════════════════════════════
 *
 * The client was re-pinned to bata-platform main at `e99b3d2` (for ADR 6063, `docs/adr/6016`),
 * which carries all three reads (platform PRs 573–575). Each `…_READ` below is now the generated
 * operation, so the pages issue their reads; the hand-typed shapes stay, and the generated
 * models are assignable to them (the typecheck is the proof). What follows is the text as it
 * stood before the pin.
 *
 * `src/api/spec/openapi.json` does not publish any of the three reads yet, so — exactly as
 * `network-health.ts` did before its re-pin (ADR 9037's "built before its read lands") — the
 * shapes below are typed by hand from the contract, each `…_READ` constant is `null`, and every
 * page draws its designed «In arrivo» state and issues NO request. The `…_PROPOSED_OPERATION`
 * constants are the operations as the contract declares them; a test mounts each page over one
 * with a stand-in `fetch`, so the whole rendering is exercised before the re-pin.
 *
 * `src/features/inseason/service-tradeoff.test.tsx` keeps the tripwire: the pinned spec has each path exactly when its
 * constant is wired. When bata-platform 570 / 571 land, the re-pin (1) re-exports the generated
 * shapes under the names below, (2) sets each `…_READ` to the generated operation, (3) keeps the
 * readers (`doorAvailabilityOf`, `exchangeRunsOf`, `serviceExchangeOf`) — the pages read ONLY
 * what they build. **Every field access of the platform's shapes is in this file.**
 *
 *   GET /api/v1/inseason/runs/{run_id}/door-availability          bata-platform 570
 *   GET /api/v1/inseason/service-exchange                          bata-platform 571 (the list)
 *   GET /api/v1/inseason/service-exchange/{forecast_run_id}        bata-platform 571 (one run)
 *
 * Assumptions beyond the issue's contract, to be checked at the re-pin:
 *   - the list read is the in-season directory's envelope (`CursorPage`: `items`, `page_size`,
 *     `has_more`, `next_cursor`) with `country_key`, `limit` and `cursor` query members, as
 *     `GET /inseason/runs` is — the items themselves camelCase as the contract states;
 *   - a serving process with no source installed answers 404 with
 *     {@link DOOR_AVAILABILITY_UNAVAILABLE} / {@link SERVICE_EXCHANGE_UNAVAILABLE}, as the
 *     network-health read does (ADR 6055) — drawn as «In arrivo», never as zeroes;
 *   - `period` is the forecast's period key (an ISO week-start date in the backtest), shown as
 *     the platform writes it.
 */

import { doorAvailability, listServiceExchange, serviceExchange, type OperationSpec } from '../generated/operations';

// ── read 1: door availability (bata-platform 570) ────────────────────────────

/** How the window's stock was observed. `mixed`: some weeks one way, some the other. */
export const OBSERVATION_BASES = ['weekly_snapshot', 'daily', 'mixed'] as const;
export type ObservationBasis = (typeof OBSERVATION_BASES)[number];

/** One door over the window, as the platform states it. */
export interface DoorAvailabilityDoorShape {
  readonly storeKey: string;
  readonly storeName: string | null;
  readonly formatCode: string | null;
  readonly climateZoneCode: string | null;
  readonly weeksObserved: number;
  readonly sizesInStock: number;
  readonly sizesOffered: number;
  /** `sizesInStock / sizesOffered` over the window's live articles; `null` when nothing was offered. */
  readonly sizeAvailability: number | null;
  /** Σ on-hand over the door's observed weeks — pair-weeks, not a stock (issue 311). */
  readonly onHandPairs: number;
  readonly pairsSold: number;
  /** Σ on-hand ÷ Σ sold; `null` when the door sold nothing. */
  readonly weeksOfCover: number | null;
  readonly liveArticlesPerWeek: number;
}

/** The read's answer: the run's 13 complete weeks up to its stock as-of week. */
export interface DoorAvailabilityShape {
  readonly runId: string;
  readonly countryKey: string;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly weeks: number;
  readonly observationBasis: ObservationBasis;
  readonly doorsWithoutSales: number;
  readonly doors: readonly DoorAvailabilityDoorShape[];
}

export interface DoorAvailabilityParams {
  readonly path: { readonly run_id: string };
}

export type DoorAvailabilityRead = OperationSpec<DoorAvailabilityParams, DoorAvailabilityShape>;

export const DOOR_AVAILABILITY_PATH_IN_SPEC = '/api/v1/inseason/runs/{run_id}/door-availability';

/** The operation as the contract declares it. Not wired: tests mount the page over it. */
export const DOOR_AVAILABILITY_PROPOSED_OPERATION: DoorAvailabilityRead = {
  operationId: 'door_availability_api_v1_inseason_runs__run_id__door_availability_get',
  method: 'GET',
  path: DOOR_AVAILABILITY_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: ['run_id'],
  queryParams: [],
  hasBody: false,
};

/** `null` until the pinned spec publishes the read — the page draws «In arrivo» and reads nothing. */
export const DOOR_AVAILABILITY_READ: DoorAvailabilityRead | null = doorAvailability;

/** 404: no door-availability source in the serving process — «In arrivo», never zeroes (assumed). */
export const DOOR_AVAILABILITY_UNAVAILABLE = 'planning.inseason.door_availability_unavailable';

export function doorAvailabilityParams(runId: string): DoorAvailabilityParams {
  return { path: { run_id: runId } };
}

/** One door as the page reads it. */
export interface DoorRow {
  readonly storeKey: string;
  readonly storeName: string | null;
  readonly format: string | null;
  readonly climateZone: string | null;
  readonly weeksObserved: number;
  readonly sizesInStock: number;
  readonly sizesOffered: number;
  readonly availability: number | null;
  /**
   * Average pairs on hand per observed week: `onHandPairs ÷ weeksObserved` (issue 311 — the read
   * sums on-hand over the weeks, so the raw figure is pair-weeks). `null` with no observed week.
   */
  readonly onHand: number | null;
  /** The read's own Σ on-hand over the observed weeks (pair-weeks) — what weeks of cover is built from. */
  readonly onHandPairWeeks: number;
  readonly sold: number;
  readonly cover: number | null;
  readonly liveArticlesPerWeek: number;
}

/** The window as the page reads it. */
export interface DoorAvailability {
  readonly runId: string;
  readonly market: string;
  readonly from: string;
  readonly to: string;
  readonly weeks: number;
  /** A basis this build has no words for is `null`: the page says the basis is not stated. */
  readonly basis: ObservationBasis | null;
  readonly doorsWithoutSales: number;
  readonly doors: readonly DoorRow[];
}

/** Pair-weeks over observed weeks: the door's average pairs on hand. Pure. */
export function averageOnHand(pairWeeks: number, weeksObserved: number): number | null {
  return weeksObserved > 0 ? pairWeeks / weeksObserved : null;
}

const isBasis = (value: string): value is ObservationBasis => (OBSERVATION_BASES as readonly string[]).includes(value);

/** The page's reading of the read. Pure. */
export function doorAvailabilityOf(page: DoorAvailabilityShape): DoorAvailability {
  return {
    runId: page.runId,
    market: page.countryKey,
    from: page.windowStart,
    to: page.windowEnd,
    weeks: page.weeks,
    basis: isBasis(page.observationBasis) ? page.observationBasis : null,
    doorsWithoutSales: page.doorsWithoutSales,
    doors: page.doors.map((door) => ({
      storeKey: door.storeKey,
      storeName: door.storeName,
      format: door.formatCode,
      climateZone: door.climateZoneCode,
      weeksObserved: door.weeksObserved,
      sizesInStock: door.sizesInStock,
      sizesOffered: door.sizesOffered,
      availability: door.sizeAvailability,
      onHand: averageOnHand(door.onHandPairs, door.weeksObserved),
      onHandPairWeeks: door.onHandPairs,
      sold: door.pairsSold,
      cover: door.weeksOfCover,
      liveArticlesPerWeek: door.liveArticlesPerWeek,
    })),
  };
}

// ── read 2: the evaluated forecast runs (bata-platform 571) ──────────────────

/** Whether the backtest has its week of actuals yet. */
export const EXCHANGE_STATUSES = ['evaluated', 'awaiting_actuals'] as const;
export type ExchangeStatus = (typeof EXCHANGE_STATUSES)[number];

export interface ServiceExchangeRunShape {
  readonly forecastRunId: string;
  readonly modelVersionId: string;
  readonly period: string;
  readonly status: ExchangeStatus;
}

/** The in-season directory's envelope (`CursorPage`, as `GET /inseason/runs`) — assumed. */
export interface ServiceExchangeRunPage {
  readonly items: readonly ServiceExchangeRunShape[];
  readonly page_size: number;
  readonly has_more?: boolean;
  readonly next_cursor?: string | null;
}

export interface ServiceExchangeListParams {
  readonly query?: {
    readonly country_key?: string | null;
    readonly limit?: number | null;
    readonly cursor?: string | null;
  };
}

export type ServiceExchangeListRead = OperationSpec<ServiceExchangeListParams, ServiceExchangeRunPage>;

export const SERVICE_EXCHANGE_LIST_PATH_IN_SPEC = '/api/v1/inseason/service-exchange';

export const SERVICE_EXCHANGE_LIST_PROPOSED_OPERATION: ServiceExchangeListRead = {
  operationId: 'list_service_exchange_api_v1_inseason_service_exchange_get',
  method: 'GET',
  path: SERVICE_EXCHANGE_LIST_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['country_key', 'limit', 'cursor'],
  hasBody: false,
};

export const SERVICE_EXCHANGE_LIST_READ: ServiceExchangeListRead | null = listServiceExchange;

/**
 * Forecast runs the page may open on by itself: one bounded page, newest first (ADR 9039 — the
 * eligibility list is a bounded page read, never a walk of every run).
 */
export const EXCHANGE_RUNS_PAGE = 24;

export function serviceExchangeListParams(): ServiceExchangeListParams {
  return { query: { limit: EXCHANGE_RUNS_PAGE } };
}

/** One forecast run as the picker reads it. */
export interface ExchangeRun {
  readonly forecastRunId: string;
  readonly modelVersionId: string;
  readonly period: string;
  /** A status this build has no word for is `null`: not offered as evaluated. */
  readonly status: ExchangeStatus | null;
}

const isStatus = (value: string): value is ExchangeStatus => (EXCHANGE_STATUSES as readonly string[]).includes(value);

/** The list as the page reads it, in the platform's order (newest first). Pure. */
export function exchangeRunsOf(page: ServiceExchangeRunPage): readonly ExchangeRun[] {
  return page.items.map((item) => ({
    forecastRunId: item.forecastRunId,
    modelVersionId: item.modelVersionId,
    period: item.period,
    status: isStatus(item.status) ? item.status : null,
  }));
}

/** The runs the page opens on by itself: the evaluated ones, newest first. Pure. */
export function evaluatedRunIds(runs: readonly ExchangeRun[]): readonly string[] {
  return runs.filter((run) => run.status === 'evaluated').map((run) => run.forecastRunId);
}

// ── read 3: one forecast run's exchange (bata-platform 571) ──────────────────

/** The rounding rules, in their declared order — the curve's lines. */
export const EXCHANGE_RULES = ['truncate', 'round_up', 'whole_pair'] as const;
export type ExchangeRule = (typeof EXCHANGE_RULES)[number];

export interface ServiceExchangePopulationShape {
  readonly cells: number;
  readonly doors: number;
  readonly articles: number;
  readonly stockedOutCells: number;
  readonly pairsSold: number;
  readonly forecastCells: number;
  readonly matchedCells: number;
  readonly unforecastPairsSold: number;
  readonly periodPairsSold: number;
  readonly actualOnHandPairs: number;
}

export interface ServiceExchangePointShape {
  readonly rule: ExchangeRule;
  readonly level: number;
  readonly fromStoredLevel: boolean;
  readonly pairsHeld: number;
  readonly pairsServed: number;
  readonly demandPairs: number;
  readonly fillRate: number | null;
  readonly cycleService: number | null;
  readonly leftoverPairs: number;
  readonly sizesStockedShare: number | null;
}

export interface ServiceExchangeShape {
  readonly forecastRunId: string;
  readonly modelVersionId: string;
  readonly countryKey: string;
  readonly period: string;
  readonly horizon: number;
  readonly status: ExchangeStatus;
  /** Why a run awaits its actuals (`period_not_ended`, `doors_not_landed`, `period_not_a_week`); `null` once evaluated. */
  readonly awaitingReason: string | null;
  /** The doors the forecast covers, and how many of them have landed rows for the period (ADR 6062). */
  readonly forecastDoors: number;
  readonly landedDoors: number;
  /** `null` while the run awaits its actuals — nothing was read yet (the pinned contract). */
  readonly population: ServiceExchangePopulationShape | null;
  readonly points: readonly ServiceExchangePointShape[];
}

export interface ServiceExchangeParams {
  readonly path: { readonly forecast_run_id: string };
}

export type ServiceExchangeRead = OperationSpec<ServiceExchangeParams, ServiceExchangeShape>;

export const SERVICE_EXCHANGE_PATH_IN_SPEC = '/api/v1/inseason/service-exchange/{forecast_run_id}';

export const SERVICE_EXCHANGE_PROPOSED_OPERATION: ServiceExchangeRead = {
  operationId: 'read_service_exchange_api_v1_inseason_service_exchange__forecast_run_id__get',
  method: 'GET',
  path: SERVICE_EXCHANGE_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: ['forecast_run_id'],
  queryParams: [],
  hasBody: false,
};

export const SERVICE_EXCHANGE_READ: ServiceExchangeRead | null = serviceExchange;

/** 404: no backtest source in the serving process — «In arrivo» (assumed, as ADR 6055). */
export const SERVICE_EXCHANGE_UNAVAILABLE = 'planning.inseason.service_exchange_unavailable';

export function serviceExchangeParams(forecastRunId: string): ServiceExchangeParams {
  return { path: { forecast_run_id: forecastRunId } };
}

/** One evaluated policy as the page reads it. */
export interface ExchangePolicy {
  /** A rule this build has no words for is dropped by {@link serviceExchangeOf}, and counted. */
  readonly rule: ExchangeRule;
  readonly level: number;
  readonly stored: boolean;
  readonly pairsHeld: number;
  readonly pairsServed: number;
  readonly demandPairs: number;
  readonly fillRate: number | null;
  readonly cycleService: number | null;
  readonly leftoverPairs: number;
  readonly sizesStockedShare: number | null;
}

/** One run's exchange as the page reads it. */
export interface ServiceExchange {
  readonly forecastRunId: string;
  readonly modelVersionId: string;
  readonly market: string;
  readonly period: string;
  readonly horizon: number;
  readonly status: ExchangeStatus | null;
  /** `null` while the run awaits its actuals. */
  readonly population: ServiceExchangePopulationShape | null;
  readonly policies: readonly ExchangePolicy[];
  /** Points under a rule this build has no words for — said on the page, never drawn unnamed. */
  readonly unknownRulePoints: number;
  /** Why the run awaits its actuals — a code this build has words for, else `other`; `null` once evaluated. */
  readonly awaitingReason: AwaitingReason | null;
  /** Partial coverage is evaluated and re-evaluated as doors land (owner ruling 2026-10-06, platform ADR 6062). */
  readonly forecastDoors: number;
  readonly landedDoors: number;
}

/** Why a run awaits its actuals, as the platform says it (ADR 6062); `other` for a code this build lacks. */
export const AWAITING_REASONS = ['period_not_ended', 'doors_not_landed', 'period_not_a_week', 'other'] as const;
export type AwaitingReason = (typeof AWAITING_REASONS)[number];
const isAwaitingReason = (value: string): value is AwaitingReason => (AWAITING_REASONS as readonly string[]).includes(value) && value !== 'other';

const isRule = (value: string): value is ExchangeRule => (EXCHANGE_RULES as readonly string[]).includes(value);

/** The page's reading of one run's exchange. Pure. */
export function serviceExchangeOf(page: ServiceExchangeShape): ServiceExchange {
  const known = page.points.filter((point) => isRule(point.rule));
  return {
    forecastRunId: page.forecastRunId,
    modelVersionId: page.modelVersionId,
    market: page.countryKey,
    period: page.period,
    horizon: page.horizon,
    status: isStatus(page.status) ? page.status : null,
    population: page.population,
    policies: known.map((point) => ({
      rule: point.rule,
      level: point.level,
      stored: point.fromStoredLevel,
      pairsHeld: point.pairsHeld,
      pairsServed: point.pairsServed,
      demandPairs: point.demandPairs,
      fillRate: point.fillRate,
      cycleService: point.cycleService,
      leftoverPairs: point.leftoverPairs,
      sizesStockedShare: point.sizesStockedShare,
    })),
    unknownRulePoints: page.points.length - known.length,
    awaitingReason: page.awaitingReason === null ? null : isAwaitingReason(page.awaitingReason) ? page.awaitingReason : 'other',
    forecastDoors: page.forecastDoors,
    landedDoors: page.landedDoors,
  };
}
